import axios, { AxiosResponse } from 'axios';
import { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import * as yup from 'yup';
import { useFormik } from 'formik';
import {
  Button,
  Divider,
  Grid,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GetAppIcon from '@mui/icons-material/GetApp';

import { MAX_FILE_SIZE, modelSettings } from '../../../settings/settings';
import { useXlsxValidation } from './useXlsxValidation';

import { EnvName, ExcelRow } from './types';
import { useFiles } from './useFiles';
import { useCharts } from './useCharts';
import './styles.scss';
import { read, utils } from 'xlsx';
import { dropdownSettingsTitles, formatValue, transformArray } from './utils';

export const InputForm = ({ modelName }: { modelName: string }) => {
  const currentModelSettings = modelSettings[modelName];

  const envName: EnvName = (process.env.REACT_APP_ENV_NAME || 'DEV') as EnvName;
  let requestUrl = currentModelSettings.apiUrl[envName];
  let authKey = process.env.REACT_APP_API_KEY || '';

  const isDownloadAllowed = currentModelSettings.isDownloadAllowed;
  const [outputFile, setOutputFile] = useState<{
    name: string;
    href: string;
  } | null>(null);
  const [outputTableData, setOutputTableData] = useState<ExcelRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isResultReceived, setIsResultReceived] = useState(false);
  const [charts, setCharts] = useState<
    { dom: HTMLDivElement | null; chart: echarts.ECharts | null }[]
  >([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [response, setResponse] = useState<AxiosResponse>();

  const chartRefs = useRef<(HTMLDivElement | null)[]>([]);
  const chartsRef = useRef(charts);

  const [receivedCharts, setReceivedCharts] = useState<any | null>();

  const [file, setFile] = useState<FileList | null>(null);
  const [sheets, setSheets] = useState<any[]>([]);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [isFileReadyforValidate, setIsFileReadyforValidate] = useState(false);

  const { isValidFileType, isContainsRequiredSheets, isSheetContentValid } =
    useXlsxValidation(currentModelSettings);

  const prevFileRef = useRef<FileList | null>(null);
  const prevSheetsRef = useRef<any[]>([]);
  const prevSheetNamesRef = useRef<string[]>([]);

  const [localChartsDropdownData, setLocalChartsDropdownData] = useState<
    | {
        [key: string]: string[][];
      }
    | undefined
  >();

  const [selectedValues, setSelectedValues] = useState<{
    [key: string]: string;
  }>({});

  const [filtersSettings, setFiltersSettings] = useState<{
    [key: string]: string;
  }>({});

  const {
    handleChartsSequenceDownload,
    handleDownloadChart,
    handleDownloadQuadCharts,
    getUpdatedChartsSettings,
    getUpdatedCharts,
    chartsDropdownData,
  } = useCharts({
    charts,
    chartsRef,
    chartRefs,
    receivedCharts,
    filtersSettings,
  });

  useEffect(() => {
    if (chartsDropdownData && !localChartsDropdownData) {
      setLocalChartsDropdownData(chartsDropdownData);
    }
  }, [chartsDropdownData]);

  const validationSchema = yup.object({
    param1: currentModelSettings.paramsMapping
      ? yup.string().required('Parameter is required')
      : yup.string().optional(),
    param2: currentModelSettings.paramsMapping
      ? yup.string().required('Parameter is required')
      : yup.string().optional(),
    param3: currentModelSettings.paramsMapping
      ? yup.string().required('Parameter is required')
      : yup.string().optional(),
    param4: currentModelSettings.paramsMapping
      ? yup.string().required('Parameter is required')
      : yup.string().optional(),
    param5: currentModelSettings.paramsMapping
      ? yup.string().required('Parameter is required')
      : yup.string().optional(),
    param6: currentModelSettings.paramsMapping
      ? yup.string().required('Parameter is required')
      : yup.string().optional(),

    file: yup
      .mixed()
      .required('Required')
      .test('is-valid-type', 'Not a valid file type', (value) => {
        const isFilesValid: boolean[] = [];
        Object.entries(value).forEach((file) => {
          isFilesValid.push(
            isValidFileType((file[1] as File).name.toLowerCase(), 'file'),
          );
        });
        return !isFilesValid.includes(false);
      })
      .test(
        'is-valid-size',
        `Max allowed size is ${MAX_FILE_SIZE}KB`,
        (value) => {
          const isFilesSizeValid: boolean[] = [];
          Object.entries(value).forEach((file) => {
            isFilesSizeValid.push(
              (file[1] as File).size <= MAX_FILE_SIZE * 1024,
            );
          });
          return !isFilesSizeValid.includes(false);
        },
      )
      .test('is-valid-content', 'Incorrect file structure', () => {
        if (!isFileReadyforValidate) return true;
        if (sheetNames.length) {
          return (
            isContainsRequiredSheets(sheetNames) && isSheetContentValid(sheets)
          );
        }
      }),
  });

  const updateChartsAfterFiltering = (response: any) => {
    const filteredChartsResponse = Object.entries(
      response.data.data.charts,
    ).filter((chart: any) => chart[0] !== 'EmissionRateFrequencyGreen'); // temporary disable charts

    const updatedChartsSettings = getUpdatedChartsSettings(
      filteredChartsResponse,
      false,
    );

    setReceivedCharts(updatedChartsSettings);
    const allCharts = updatedChartsSettings.map(() => ({
      dom: null,
      chart: null,
    }));

    setCharts(allCharts);
    chartsRef.current = allCharts;
    chartRefs.current = chartRefs.current.slice(
      0,
      updatedChartsSettings.length,
    );
  };

  const proceedResponse = (response: AxiosResponse<any, any>) => {
    setIsLoading(false);
    setIsResultReceived(true);
    setLocalChartsDropdownData(undefined);

    setResponse(response);

    const url = generateOutputFile(response);

    let workbook = read(response.data.data.file, { type: 'binary' });

    const sheetName = workbook.SheetNames[2];
    const sheet = workbook.Sheets[sheetName];
    const jsonData = utils.sheet_to_json<ExcelRow>(sheet);
    setOutputTableData(jsonData);

    setOutputFile({
      name: 'output-file.xlsx',
      href: url,
    });

    if (!response.data.data.charts) {
      return;
    }

    const filteredChartsResponse = Object.entries(
      response.data.data.charts,
    ).filter((chart: any) => chart[0] !== 'EmissionRateFrequencyGreen'); // temporary disable chart

    const updatedChartsSettings = getUpdatedChartsSettings(
      filteredChartsResponse,
    );

    setReceivedCharts(updatedChartsSettings);
    const allCharts = updatedChartsSettings.map(() => ({
      dom: null,
      chart: null,
    }));

    setCharts(allCharts);
    chartsRef.current = allCharts;
    chartRefs.current = chartRefs.current.slice(
      0,
      updatedChartsSettings.length,
    );
  };

  const formik = useFormik({
    initialValues: {
      param1: '',
      param2: '',
      param3: '',
      param4: '',
      param5: '',
      param6: '',
      file: null as FileList | null,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const formData = new FormData();
      if (values.file) {
        for (let i = 0; i < values.file.length; i++) {
          formData.append(currentModelSettings.fileField, values.file[i]);
        }
      }

      values.param1 && formData.append('param1', values.param1);
      values.param2 && formData.append('param2', values.param2);
      values.param3 && formData.append('param3', values.param3);
      values.param4 && formData.append('param4', values.param4);
      values.param5 && formData.append('param5', values.param5);
      values.param6 && formData.append('param6', values.param6);
      formData.append(
        'paramsMapping',
        JSON.stringify(currentModelSettings.paramsMapping),
      );

      setIsLoading(true);
      setOutputFile(null);
      setOutputTableData([]);
      setErrorMessage('');
      setReceivedCharts(null);
      setCharts([]);
      chartRefs.current = [];
      chartsRef.current = [];

      axios
        .post(requestUrl, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Methods': 'DELETE, POST, GET, OPTIONS',
            'x-functions-key': authKey,
          },
          responseEncoding: 'latin1',
        })
        .then((response) => {
          proceedResponse(response);
        })
        .catch((error) => {
          console.error('Error:', error);
          setIsLoading(false);
          setIsResultReceived(false);
          setErrorMessage(error.message);
        });
    },
  });

  const { generateOutputFile, handleFileChange, processFile, handleDownload } =
    useFiles({
      currentModelSettings,
      setSheets,
      setSheetNames,
      setIsFileReadyforValidate,
      setFile,
      formik,
    });

  const handleDropdownChange = (key: string, value: string) => {
    setSelectedValues((prev) => ({ ...prev, [key]: value }));
  };

  const filterResponse = (filterSettings: any, charts: any) => {
    const extractedFilterSettings: string[] = [];
    const filteredData: any[] = [];

    Object.entries(filterSettings).forEach((elem: any) => {
      extractedFilterSettings.push(elem[0].split('-')[0]);
      extractedFilterSettings.push(elem[1]);
    });
    const uniqFilters = extractedFilterSettings;
    const uniqFiltersArray = transformArray(uniqFilters);

    const separatedData = charts['EmissionRateFrequencyRed'].data.reduce(
      (acc: { [x: string]: any[] }, item: any[][]) => {
        const survey = item[0][3];
        if (!acc[survey]) {
          acc[survey] = [];
        }
        acc[survey].push(item);
        return acc;
      },
      {},
    );

    Object.entries(separatedData).forEach((currentChartData: any) => {
      if (
        uniqFilters.length === 0 ||
        uniqFilters.indexOf(currentChartData[0]) < 0
      ) {
        filteredData.push(currentChartData[1]);
      } else {
        const collectArray: any[] = [];
        const arr = uniqFiltersArray.filter(
          (filter) => filter.indexOf(currentChartData[0]) >= 0,
        );

        arr.forEach((currentChartFilter) => {
          currentChartData[1].forEach((el: (string | string[])[]) => {
            const matchesAllFilters = currentChartFilter.every((filterItem) => {
              if (filterItem === currentChartData[0]) return true;
              return (
                el[0].indexOf(filterItem) >= 0 ||
                el[0].indexOf(filterItem.toString()) >= 0
              );
            });

            if (matchesAllFilters) {
              collectArray.push(el);
            }
          });
        });

        if (collectArray.length) {
          filteredData.push(collectArray);
        } else {
          filteredData.push(currentChartData[1]);
        }
      }
    });
    return Array.from(new Set(filteredData.flat()));
  };

  useEffect(() => {
    if (!selectedValues || !response) return;
    const responseCopy = JSON.parse(JSON.stringify(response));
    const charts = responseCopy?.data.data.charts;
    if (charts) {
      const filteredResponse = filterResponse(selectedValues, charts);
      if (charts.EmissionRateFrequencyRed) {
        charts.EmissionRateFrequencyRed.data = filteredResponse;
      }

      responseCopy && updateChartsAfterFiltering(responseCopy);
      setFiltersSettings(selectedValues);
    }
  }, [selectedValues, response]);

  useEffect(() => {
    if (receivedCharts) {
      const updatedCharts = getUpdatedCharts();
      setTimeout(() => {
        chartsRef.current = updatedCharts;
        setCharts(updatedCharts);
      }, 1000);
    }
  }, [receivedCharts, getUpdatedCharts]);

  useEffect(() => {
    if (file && prevFileRef.current !== file) {
      setSheets([]);
      setSheetNames([]);
      Object.entries(file).forEach((file) => {
        processFile(file[1]);
      });
    }
  }, [file, processFile]);

  useEffect(() => {
    prevFileRef.current = file;
    prevSheetsRef.current = sheets;
    prevSheetNamesRef.current = sheetNames;
  });

  useEffect(() => {
    if (
      isFileReadyforValidate &&
      sheetNames.length &&
      prevSheetNamesRef.current !== sheetNames
    ) {
      formik.validateField('file');
    }
  }, [isFileReadyforValidate, sheetNames, sheets, formik]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid
        container
        spacing={3}
        padding={3}
        alignItems={'top'}
        justifyItems={'center'}
      >
        <Grid
          container
          spacing={3}
          padding={3}
          alignItems={'top'}
          justifyItems={'center'}
          alignContent={'flex-start'}
          xs={12}
          sm={isResultReceived ? 3 : 9}
        >
          <Grid item xs={12} sm={12}>
            <Divider>
              <h3>Input parameters</h3>
            </Divider>
          </Grid>
          {isDownloadAllowed && (
            <>
              <Grid item xs={12} sm={isResultReceived ? 12 : 4}>
                <Button
                  fullWidth={true}
                  variant="contained"
                  onClick={handleDownload}
                >
                  Download
                </Button>
              </Grid>
              <Grid item xs={12} sm={8} hidden={isResultReceived}>
                Click to download excel template optimized for use in this model
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={isResultReceived ? 12 : 3}>
            <TextField
              fullWidth={true}
              name="file"
              type="file"
              onChange={handleFileChange}
              onBlur={formik.handleBlur}
              error={formik.touched.file && Boolean(formik.errors.file)}
              helperText={formik.touched.file && formik.errors.file}
              inputProps={{
                accept:
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                multiple: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={isResultReceived ? 0 : 9}>
            <label htmlFor={'upload'} hidden={isResultReceived}>
              Select to upload formatted excel optimized for use in this model
            </label>
          </Grid>
          {currentModelSettings.paramsMapping && (
            <>
              <Grid
                container
                spacing={isResultReceived ? 1 : 2}
                padding={3}
                alignItems={'top'}
                justifyItems={'center'}
                alignContent={'flex-start'}
                xs={12}
              >
                <Grid item sm={6} xs={12}>
                  <Grid
                    container
                    spacing={isResultReceived ? 0 : 3}
                    padding={0}
                    alignItems={'top'}
                    justifyItems={'left'}
                  >
                    <Grid item xs={12} sm={isResultReceived ? 12 : 4}>
                      <TextField
                        name="param1"
                        id="param1"
                        label={currentModelSettings.paramsMapping.param1.label}
                        defaultValue=""
                        value={formik.values.param1}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.param1 && Boolean(formik.errors.param1)
                        }
                        fullWidth={true}
                      />
                    </Grid>
                    <Grid item xs={12} sm={isResultReceived ? 0 : 8}>
                      <label htmlFor={'param1'} hidden={isResultReceived}>
                        {currentModelSettings.paramsMapping.param1.description}
                      </label>
                      <FormHelperText sx={{ color: '#d32f2f' }}>
                        {formik.touched.param1 && formik.errors.param1}
                      </FormHelperText>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Grid
                    container
                    spacing={isResultReceived ? 0 : 3}
                    padding={0}
                    alignItems={'top'}
                    justifyItems={'left'}
                  >
                    <Grid item xs={12} sm={isResultReceived ? 12 : 4}>
                      <TextField
                        name="param2"
                        id="param2"
                        label={currentModelSettings.paramsMapping.param2.label}
                        defaultValue=""
                        value={formik.values.param2}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.param2 && Boolean(formik.errors.param2)
                        }
                        fullWidth={true}
                      />
                    </Grid>
                    <Grid item xs={12} sm={isResultReceived ? 0 : 8}>
                      <label htmlFor={'param2'} hidden={isResultReceived}>
                        {currentModelSettings.paramsMapping.param2.description}
                      </label>
                      <FormHelperText sx={{ color: '#d32f2f' }}>
                        {formik.touched.param2 && formik.errors.param2}
                      </FormHelperText>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Grid
                    container
                    spacing={isResultReceived ? 0 : 3}
                    padding={0}
                    alignItems={'top'}
                    justifyItems={'left'}
                  >
                    <Grid item xs={12} sm={isResultReceived ? 12 : 4}>
                      <TextField
                        name="param3"
                        id="param3"
                        label={currentModelSettings.paramsMapping.param3.label}
                        defaultValue=""
                        value={formik.values.param3}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.param3 && Boolean(formik.errors.param3)
                        }
                        fullWidth={true}
                      />
                    </Grid>
                    <Grid item xs={12} sm={isResultReceived ? 0 : 8}>
                      <label htmlFor={'param3'} hidden={isResultReceived}>
                        {currentModelSettings.paramsMapping.param3.description}
                      </label>
                      <FormHelperText sx={{ color: '#d32f2f' }}>
                        {formik.touched.param3 && formik.errors.param3}
                      </FormHelperText>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Grid
                    container
                    spacing={isResultReceived ? 0 : 3}
                    padding={0}
                    alignItems={'top'}
                    justifyItems={'left'}
                  >
                    <Grid item xs={12} sm={isResultReceived ? 12 : 4}>
                      <TextField
                        name="param5"
                        id="param5"
                        label={currentModelSettings.paramsMapping.param5.label}
                        defaultValue=""
                        value={formik.values.param5}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.param5 && Boolean(formik.errors.param5)
                        }
                        fullWidth={true}
                      />
                    </Grid>
                    <Grid item xs={12} sm={isResultReceived ? 0 : 8}>
                      <label htmlFor={'param5'} hidden={isResultReceived}>
                        {currentModelSettings.paramsMapping.param5.description}
                      </label>
                      <FormHelperText sx={{ color: '#d32f2f' }}>
                        {formik.touched.param5 && formik.errors.param5}
                      </FormHelperText>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Grid
                    container
                    spacing={isResultReceived ? 0 : 3}
                    padding={0}
                    alignItems={'top'}
                    justifyItems={'left'}
                  >
                    <Grid item xs={12} sm={isResultReceived ? 12 : 4}>
                      <TextField
                        name="param6"
                        id="param6"
                        label={currentModelSettings.paramsMapping.param6.label}
                        defaultValue=""
                        value={formik.values.param6}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.param6 && Boolean(formik.errors.param6)
                        }
                        fullWidth={true}
                      />
                    </Grid>
                    <Grid item xs={12} sm={isResultReceived ? 0 : 8}>
                      <label htmlFor={'param6'} hidden={isResultReceived}>
                        {currentModelSettings.paramsMapping.param6.description}
                      </label>
                      <FormHelperText sx={{ color: '#d32f2f' }}>
                        {formik.touched.param6 && formik.errors.param6}
                      </FormHelperText>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Grid
                    container
                    spacing={isResultReceived ? 0 : 3}
                    padding={0}
                    alignItems={'top'}
                    justifyItems={'left'}
                  >
                    <Grid item xs={12} sm={isResultReceived ? 12 : 4}>
                      {currentModelSettings.paramsMapping.param4.values && (
                        <FormControl fullWidth>
                          <InputLabel id="param4-label">
                            {currentModelSettings.paramsMapping.param4.label}
                          </InputLabel>
                          <Select
                            labelId="param4-label"
                            id="param4"
                            label={
                              currentModelSettings.paramsMapping.param4.label
                            }
                            value={formik.values.param4}
                            onChange={(event) => {
                              formik.setFieldValue(
                                'param4',
                                event.target.value,
                              );
                            }}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.param4 &&
                              Boolean(formik.errors.param4)
                            }
                            fullWidth={true}
                          >
                            {currentModelSettings.paramsMapping.param4.values.map(
                              (option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ),
                            )}
                          </Select>
                        </FormControl>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={isResultReceived ? 0 : 8}>
                      <label htmlFor={'param4'} hidden={isResultReceived}>
                        {currentModelSettings.paramsMapping.param4.description}
                      </label>
                      <FormHelperText sx={{ color: '#d32f2f' }}>
                        {formik.touched.param4 && formik.errors.param4}
                      </FormHelperText>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : (
                currentModelSettings.submitButtonLabel
              )}
            </Button>

            {errorMessage && (
              <Alert severity="error" sx={{ marginTop: '10px' }}>
                {errorMessage}
              </Alert>
            )}
          </Grid>
        </Grid>
        <Grid
          container
          spacing={3}
          padding={3}
          alignItems={'top'}
          justifyItems={'center'}
          xs={12}
          sm={isResultReceived ? 9 : 3}
        >
          <Grid item xs={12}>
            <Divider>
              <h3>Output</h3>
            </Divider>

            <>
              {outputFile && (
                <p>
                  <b>Output report:</b>{' '}
                  <a href={outputFile.href}>{outputFile.name}</a>
                </p>
              )}
              {outputTableData.length > 0 && (
                <>
                  <h4>Bridger Site Level:</h4>
                  <div className={'table-container'}>
                    <table>
                      <thead>
                        <tr>
                          {Object.keys(outputTableData[0]).map((key) => (
                            <th key={key}>{key}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {outputTableData.map((row, index) => (
                          <tr key={index}>
                            {Object.values(row).map((value, idx) => {
                              const { display, full } = formatValue(value);
                              return (
                                <td
                                  key={idx}
                                  title={
                                    full !== undefined ? full.toString() : ''
                                  }
                                >
                                  {display}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {(receivedCharts || []).length > 0 && (
                <p>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleChartsSequenceDownload}
                  >
                    Download Charts Data and Images
                  </Button>
                </p>
              )}
            </>
            {receivedCharts && <Divider sx={{ margin: '20px 0' }} />}
            <div className="charts-container">
              {charts.map((chart, index) => {
                if (!receivedCharts) return <></>;
                const chartType = receivedCharts[index].chart_type;
                if (chartType === 'primary') {
                  const measurement =
                    receivedCharts[index].measurement &&
                    receivedCharts[index].measurement[0];
                  Object.entries(localChartsDropdownData || {}).map(
                    ([, values]) => {
                      return Object.entries(values)
                        .filter(([innerKey]) => {
                          return innerKey === measurement;
                        })
                        .map(([, innerValues]) => {
                          return innerValues;
                        });
                    },
                  );
                  return (
                    <>
                      <div className="dropdown-container">
                        {Object.entries(localChartsDropdownData || {}).map(
                          ([key, values]) => (
                            <div key={key} className="dropdown-row">
                              {Object.entries(values)
                                .filter(
                                  ([innerKey]) => innerKey === measurement,
                                )
                                .flatMap(([innerKey, innerValues]) =>
                                  (innerValues as unknown as string[][]).map(
                                    (array, arrayIndex) =>
                                      array.length > 1 && (
                                        <FormControl
                                          fullWidth
                                          key={`${innerKey}-${arrayIndex}`}
                                          className="dropdown-item"
                                        >
                                          <InputLabel
                                            id={`${innerKey}-${arrayIndex}-label`}
                                          >
                                            {dropdownSettingsTitles[arrayIndex]}
                                          </InputLabel>
                                          <Select
                                            labelId={`${innerKey}-${arrayIndex}-label`}
                                            id={`${innerKey}-${arrayIndex}`}
                                            label={`${innerKey} ${arrayIndex + 1}`}
                                            value={
                                              selectedValues[
                                                `${innerKey}-${arrayIndex}`
                                              ] || ''
                                            }
                                            onChange={(e) =>
                                              handleDropdownChange(
                                                `${innerKey}-${arrayIndex}`,
                                                e.target.value as string,
                                              )
                                            }
                                          >
                                            <MenuItem key={999} value={''}>
                                              All{' '}
                                              {
                                                dropdownSettingsTitles[
                                                  arrayIndex
                                                ]
                                              }
                                              s
                                            </MenuItem>
                                            {array.map((value, index) => (
                                              <MenuItem
                                                key={index}
                                                value={value}
                                              >
                                                {value}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      ),
                                  ),
                                )}
                            </div>
                          ),
                        )}
                      </div>
                      <div
                        key={index}
                        ref={(el) => (chartRefs.current[index] = el)}
                        style={{ width: '100%', height: '400px' }}
                      />
                      <Divider sx={{ margin: '16px 0' }} />
                    </>
                  );
                }
                return null;
              })}
              {charts.map((chart, index) => {
                if (!receivedCharts) return <></>;
                const chartType = receivedCharts[index].chart_type;
                if (chartType === 'secondary') {
                  return (
                    <Accordion key={index}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`panel${index}-content`}
                        id={`panel${index}-header`}
                      >
                        {receivedCharts[index].title.text}
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadChart(index);
                          }}
                          aria-label="download"
                        >
                          <GetAppIcon />
                        </IconButton>
                      </AccordionSummary>
                      <AccordionDetails>
                        <div
                          ref={(el) => (chartRefs.current[index] = el)}
                          style={{ width: '100%', height: '400px' }}
                        />
                      </AccordionDetails>
                    </Accordion>
                  );
                }
                return null;
              })}
              {(() => {
                const quadCharts: JSX.Element[] = [];
                let currentQuadCharts: JSX.Element[] = [];
                let currentAccordionKey = 0;
                let startIndex = 0;

                charts.forEach((chart, index) => {
                  if (!receivedCharts) return;
                  const chartType = receivedCharts[index].chart_type;
                  if (chartType.toString().startsWith('quad-charts')) {
                    if (currentQuadCharts.length === 0) {
                      startIndex = index;
                    }
                    currentQuadCharts.push(
                      <div
                        key={index}
                        ref={(el) => (chartRefs.current[index] = el)}
                        style={{ width: '500px', height: '400px' }}
                        className={chartType}
                      />,
                    );
                    if (chartType === 'quad-charts clear') {
                      const endIndex = index;
                      quadCharts.push(
                        <Accordion key={`quad-${currentAccordionKey}`}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel${currentAccordionKey}-content`}
                            id={`panel${currentAccordionKey}-header`}
                          >
                            {receivedCharts[index].title.text}
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadQuadCharts(startIndex, endIndex);
                              }}
                              aria-label="download"
                            >
                              <GetAppIcon />
                            </IconButton>
                          </AccordionSummary>
                          <AccordionDetails>
                            <div>{currentQuadCharts}</div>
                          </AccordionDetails>
                        </Accordion>,
                      );
                      currentQuadCharts = [];
                      currentAccordionKey++;
                    }
                  }
                });

                return quadCharts;
              })()}
            </div>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};
